<template>
  <transition name="fade" mode="out-in">
    <AgGridVue
      :key="key"
      style="flex: 1;"
      :grid-options="options"
      :rowData="rowData"
      :overlayNoRowsTemplate="noRowsTemplate"
      :animate-rows="true"
      @row-clicked="goToSpace"
      @grid-ready="onGridReady"
      @selection-changed="onSelectionChanged"
      @first-data-rendered="onFirstDataRendered"
    />
  </transition>
</template>

<script setup lang="ts">
import { useDark } from '@vueuse/core';
import { AgGridVue } from 'ag-grid-vue3'
import { AgEventType, ColDef, ColGroupDef, GridApi, GridOptions, RowEvent } from 'ag-grid-community';
import { themeQuartz, colorSchemeDark } from 'ag-grid-community';
import { useSpaces } from '@/queries/space.query.js';
import { campaignsApi } from '@/api/campaigns.api.js';
import { useQueries } from '@tanstack/vue-query';
import { Space, SpaceRow } from '@/types/space.js';
import SpaceActions from './cells/SpaceActions.vue';
import { useRoles } from '@/queries/user.query.js';

const isDark = useDark();
const { locale, t } = useI18n();
const appStore = useAppStore();
const router = useRouter();

let gridApi: GridApi;
const { canWriteSpaces } = usePermissions();
const { data: spaces, isPending: isSpacesPending } = useSpaces();
const { data: roles, isPending: isRolesPending } = useRoles();

const noRowsTemplate = ref(`<div>${t('app.tables.spacesTable.noRowTemplate')}</div>`);

const props = withDefaults(defineProps<{
  search: {
    value: string,
    searchable: string[],
  }
}>(), {
  search: () => {
    return {
      value: "",
      searchable: [
        "name",
      ],
    }
  }
});

const emit = defineEmits<{
  (emit: "edit-space", value: Space): void;
}>();

const key = computed(() => {
  let k = 'spaces-table';
  if (isDark.value) k = k + '-dark';
  else k = k + '-light';

  if (isSpacesPending.value || isRolesPending.value) k = k + '-pending';
  else k = k + '-ready';
  return k;
});

const sortedSpaces = computed(() => {
  if (isSpacesPending.value) return [];
  return [...spaces.value].filter((s) => s.name.toLocaleLowerCase().includes(props.search.value.toLocaleLowerCase())).sort((a, b) => a.name.localeCompare(b.name));
});

const queries = computed(() => {
  if (isSpacesPending.value) return [];
  return spaces.value.map((space) =>  {
    return {
      queryKey: ["campaigns", { organisationId: appStore.currentOrganisationId, spaceId: space.workspace_id }],
      queryFn: () => campaignsApi.getAll(appStore.currentOrganisationId, space.workspace_id, false),
    }
  });
});

const spaceCampaignsQueries = useQueries({ queries: queries });

function getNumberOfCampaigns(spaceId: string) {
  if (!spaceCampaignsQueries?.value || spaceCampaignsQueries?.value.length === 0) return 0;
  const campaigns = spaceCampaignsQueries.value.filter((v) => !v.isPending && v.data.some((v) => v.campaign.parent_workspace_id === spaceId)).flatMap((v) => v.data);
  return campaigns.length || 0;
}

const options = computed(() => {
  if (isRolesPending.value) return undefined;
  let columnDefs: (ColDef<SpaceRow, any> | ColGroupDef<SpaceRow>)[] = [
    { field: 'name', headerName: t('other.table.columns.workspaceName'), flex: 1 },
    { field: 'campaign_count', headerName: t('other.table.columns.campaignCount'), flex: 0 },
    { field: 'date', headerName: t('other.table.columns.createdAt'), flex: 0,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Intl.DateTimeFormat(locale.value, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).format(new Date(params.value));
      },
    },
  ];

  if (canWriteSpaces.value) {
    columnDefs.push({
      headerName: 'Actions',
      sortable: false,
      filter: false,
      flex: 0,
      headerClass: "header-right",
      cellRenderer: SpaceActions,
      cellRendererParams: {
        onEdit: (row) => editSpace(row),
      }
    });
  }

  return {
    theme: isDark.value ? themeQuartz.withParams({
      accentColor: '#F2CB36',
      oddRowBackgroundColor: "#1F1F1F",
      fontFamily: 'Inter, sans-serif',
    }).withPart(colorSchemeDark) : themeQuartz.withParams({
      accentColor: '#F2CB36',
      oddRowBackgroundColor: "#FAFAFA",
      fontFamily: 'Inter, sans-serif',
    }),
    suppressCellFocus: true,
    defaultColDef: {
      sortable: true,
      flex: 1,
      resizable: false,
      unSortIcon: true
    },
    columnDefs: columnDefs,
  } as GridOptions<SpaceRow>;
});

const rowData = computed(() => {
  if (isSpacesPending.value) return [];
  return sortedSpaces?.value?.map((v, index) => {
    return {
      index: index,
      id: v.workspace_id,
      name: v.name,
      date: v.creation_time,
      campaign_count: getNumberOfCampaigns(v.workspace_id),
    } as SpaceRow;
  });
});

function goToSpace(event: RowEvent<AgEventType, SpaceRow>) {
  router.push({
    name: 'campaign-selector',
    params: {
      spaceId: event.data.id,
    },
  });
}

function editSpace(row: any) {
  if (row?.index == undefined || row?.index === null) return;
  emit('edit-space', sortedSpaces.value[row?.index]);
}

function onGridReady(params) {
  gridApi = params.api
}

function onSelectionChanged() {
  // const selectedRows = gridApi.getSelectedRows();
  // console.log('Selected:', selectedRows);
}

function onFirstDataRendered() {
  gridApi.autoSizeColumns(['campaign_count']);
  gridApi.autoSizeColumns(['date']);
  gridApi.autoSizeColumns(['actions']);
}
</script>

<style lang="scss">
</style>